/**
 * VEHICLES, TEAM, SETTINGS — the three that are their own place.
 *
 * Together in one file because they are the same shape: read a table,
 * show it, let one row open. Splitting them into three would be three
 * imports and three near-identical bodies, which is how the last system
 * ended up with two parts lookups that shared no code.
 */

import { db } from "../data/client.ts";
import { hasRentals } from "../domain/side.ts";
import { rentalCard } from "./rentalfleet.ts";
import { STATUS_LABEL, forgetLabourRate, forgetEpaId, forgetShopReviewLink, shopTrade, type Status } from "../data/jobs.ts";
import { words, cap, type Vocabulary } from "../domain/trade.ts";
import { esc, money, ago, mount } from "../ui/dom.ts";
import { editable, bindEditables } from "../ui/editable.ts";
import { api } from "../data/client.ts";
import type { Role } from "../domain/permissions.ts";
import { shopWords } from "../ui/words.ts";
import { warrantyPrompt, mileageLooksWrongForTrade } from "../domain/warranty.ts";
import { primeZone } from "../ui/shopclock.ts";
import { manualsHref, manualsBlurb } from "../domain/manuals.ts";
import { since } from "../ui/dom.ts";
import { RV_CLASSES, rvArt, rvClass, rvClassOf, rvTrimFilter, type RvClassKey } from "../domain/rvclass.ts";

/**
 * WHO MAY CHANGE THE SHOP ITSELF.
 *
 * The labor rate is the one that matters: every quote prices off it, so a
 * technician nudging it changes what every customer is charged from that
 * moment on. Admin and supervisor only.
 */
const MAY_SET_SHOP: Role[] = ["admin", "supervisor"];

async function updateAccountField(id: string, column: string, value: string): Promise<void> {
  const sb = await db();
  // A rate is a number. An empty string in a numeric column is an error
  // from the database rather than a sentence a person can read, so it is
  // turned into one here.
  let v: string | number | null = value.trim() || null;
  if (column === "labour_rate") {
    const n = Number(value);
    if (!(n > 0)) throw new Error("A labor rate has to be a number greater than zero.");
    v = n;
  }
  // The database fences both of these, and a check constraint violation
  // reaches the screen as "accounts_epa_id_plausible" which tells the
  // person nothing. Say it in words before it gets that far. Clearing a
  // field back to empty stays allowed: v is already null by then.
  if (column === "epa_id" && typeof v === "string") {
    v = v.toUpperCase();
    if (!/^[A-Za-z0-9][A-Za-z0-9 -]{4,29}$/.test(v)) {
      throw new Error(
        "An EPA ID is letters and numbers, five to thirty characters, "
        + "like CAL000123456. Type it exactly as it appears on your paperwork.",
      );
    }
  }
  // A review link goes out in a text to a customer, so it has to be a web
  // address and nothing else. The job page ignores anything that is not.
  if (column === "review_url" && typeof v === "string" && !/^https?:\/\/\S+$/i.test(v)) {
    throw new Error(
      "Paste the whole link, starting with https://. In your Google Business "
      + "Profile, press Ask for reviews and copy the link it gives you.",
    );
  }
  if (column === "warranty_terms" && typeof v === "string" && v.length > 1200) {
    throw new Error(
      `That is ${v.length} characters and the invoice has room for 1,200. `
      + "Keep it to the promise itself and link to the full terms.",
    );
  }
  const { error } = await sb.from("accounts").update({ [column]: v }).eq("id", id);
  if (error) throw new Error(error.message);
  // The quote editor caches the rate for the session. Without this, a rate
  // changed here would not reach a quote until somebody reloaded, which is
  // the kind of gap that gets blamed on the person rather than the code.
  if (column === "labour_rate") forgetLabourRate();
  // Same trap, same fix: the invoice screen caches the EPA id for the
  // session, so without this the nag would keep telling somebody to add
  // the number they have just added.
  if (column === "epa_id") forgetEpaId();
  // And again for the zone every screen reads a DAY in. It is primed once
  // at boot in src/ui/shopclock.ts, so a shop that corrects its time zone
  // here would otherwise keep the old one -- and a schedule that still
  // rolls over at the wrong hour after you just fixed the setting is the
  // kind of thing people stop trusting the setting over.
  if (column === "timezone") primeZone(typeof v === "string" ? v : null);
  // The job page reads the review link once a session too.
  if (column === "review_url") forgetShopReviewLink();
}

/* ── vehicles ─────────────────────────────────────────────────────── */

type Veh = {
  id: string; year: number | null; make: string | null; model: string | null;
  description: string | null; vin: string | null; license_plate: string | null;
  mileage: number | null; colour: string | null; created_at: string;
  /** An RV's class lives here. See src/domain/rvclass.ts. */
  trim: string | null;
  customers: { id: string; full_name: string } | null;
};

/* The words come from the shop's trade, so a garage door shop reads
   "Unidentified door" here rather than being told it has a vehicle. */
const name = (v: Veh, w: Vocabulary) =>
  [v.year, v.make, v.model].filter(Boolean).join(" ").trim() || v.description || w.unidentified;

let vState: { search: string; offset: number; cls: RvClassKey | null } =
  { search: "", offset: 0, cls: null };
const VPAGE = 50;

export async function vehiclesPage(): Promise<void> {
  const trade = await shopTrade();
  const w = words(trade);
  const rv = trade === "rv";
  if (!rv) vState.cls = null;
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
        <h1>${esc(cap(w.things))}</h1><p>Every one on the books. Search by ${esc(w.searchBy)}.</p></header>
      ${rv ? rvTiles() : ""}
      <div class="searchbar"><label class="field"><span>Search</span><input id="veh-q" type="search"
        value="${esc(vState.search)}" autocomplete="off"></label></div>
      <div class="card" id="veh-list"><div class="loading">Reading&hellip;</div></div>
      <div class="pager" id="veh-pager"></div>
    </div>`);

  const input = document.getElementById("veh-q") as HTMLInputElement | null;
  let t: ReturnType<typeof setTimeout>;
  input?.addEventListener("input", () => {
    clearTimeout(t);
    t = setTimeout(() => { vState = { ...vState, search: input.value, offset: 0 }; void drawVehicles(); }, 220);
  });
  if (rv) {
    document.getElementById("rv-tiles")?.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-cls]");
      if (!b) return;
      const cls = b.dataset["cls"] as RvClassKey;
      vState = { ...vState, cls: vState.cls === cls ? null : cls, offset: 0 };
      document.querySelectorAll<HTMLButtonElement>("#rv-tiles button[data-cls]").forEach((x) => {
        const on = x.dataset["cls"] === vState.cls;
        x.classList.toggle("on", on);
        x.setAttribute("aria-pressed", String(on));
      });
      void drawVehicles();
    });
  }
  await drawVehicles();
}

/**
 * THE RV LOT, AS PICTURES.
 *
 * Anthony, 2026-09-26: "RV photo tiles on mobile." One tile per class,
 * each a picture of that kind of RV, and tapping one narrows the list to
 * it; tapping it again lets go. Two across on a phone, where a thumb can
 * hit them, and a single row on a desk.
 *
 * A filter and not a set of pages, because the question at the counter is
 * "which fifth wheels have we had in", and the answer is this same list
 * with the others taken out -- search still works inside it.
 */
function rvTiles(): string {
  return `<div class="rvtiles" id="rv-tiles" role="group" aria-label="Filter by class">${
    RV_CLASSES.map((c) => `
      <button type="button" class="rvtile${vState.cls === c.key ? " on" : ""}" data-cls="${c.key}"
        aria-pressed="${vState.cls === c.key}">
        <img src="${rvArt(c.key)}" alt="" decoding="async" loading="lazy">
        <span>${esc(c.label)}</span>
      </button>`).join("")}</div>`;
}

/** The class picture at the head of an RV's row, so the list reads as a lot. */
const rvRowArt = (v: Veh): string =>
  `<img class="rvthumb" src="${rvArt(rvClassOf(v.trim, v.model, v.description))}" alt="" decoding="async" loading="lazy">`;

async function drawVehicles(): Promise<void> {
  const host = document.getElementById("veh-list");
  const pager = document.getElementById("veh-pager");
  if (!host) return;
  const trade = await shopTrade();
  const w = words(trade);
  const rv = trade === "rv";
  try {
    const sb = await db();
    const term = vState.search.trim();
    let q = sb.from("vehicles")
      .select("id,year,make,model,trim,description,vin,license_plate,mileage,colour,created_at,customers(id,full_name)",
              { count: "exact" })
      .is("archived_at", null)
      .order("created_at", { ascending: false })
      .range(vState.offset, vState.offset + VPAGE - 1);
    /* An RV is searched by its class as well, because "fifth wheel" is
       how its owner describes it on the phone. Only for RV: for a car the
       trim is "LT" and "Sport", and a search for "lt" finding every Volt
       would be a change nobody asked for. */
    const bySearch = term
      ? `make.ilike.%${term}%,model.ilike.%${term}%,license_plate.ilike.%${term}%,vin.ilike.%${term}%,description.ilike.%${term}%`
        + (rv ? `,trim.ilike.%${term}%` : "")
      : "";
    const byClass = rv && vState.cls ? rvTrimFilter(vState.cls) : "";
    /* Both at once is an AND of two ORs. PostgREST says that as one `or`
       holding a single `and`, rather than two `or` parameters whose
       combination depends on the version in front of the database. */
    if (bySearch && byClass) q = q.or(`and(or(${bySearch}),or(${byClass}))`);
    else if (bySearch || byClass) q = q.or(bySearch || byClass);
    const { data, error, count } = await q;
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as unknown as Veh[];

    host.innerHTML = rows.length ? rows.map((v) => `
      <a class="row" href="${v.customers ? `#/customer/${v.customers.id}` : "#/vehicles"}">
        ${rv ? rvRowArt(v) : ""}
        <div class="row-main">
          <div class="row-title"><span class="num">${esc(name(v, w))}</span>
            ${w.hasPlate && v.license_plate
              ? `<span class="pill">${esc(v.license_plate)}</span>`
              : w.hasColour && v.colour ? `<span class="pill">${esc(v.colour)}</span>` : ""}</div>
          <div class="row-sub">${esc(v.customers?.full_name ?? "No owner on file")}${
            v.vin ? `<span class="dot">&middot;</span><span class="mono">${esc(v.vin)}</span>` : ""}</div>
        </div>
        <div class="row-side"><div class="when">${w.reading && v.mileage ? `${v.mileage.toLocaleString()} mi` : ""}</div></div>
      </a>`).join("") : `<div class="empty">No ${esc(w.thing)} matches that${
        rv && vState.cls ? ` in ${esc(rvClass(vState.cls)?.label ?? "")}` : ""}.</div>`;

    if (pager) {
      const total = count ?? 0;
      const to = Math.min(vState.offset + VPAGE, total);
      pager.innerHTML = `
        <button class="btn sm" id="vprev" ${vState.offset === 0 ? "disabled" : ""}>&larr; Back</button>
        <span>${total ? vState.offset + 1 : 0}&ndash;${to} of ${total.toLocaleString()}</span>
        <button class="btn sm" id="vnext" ${to >= total ? "disabled" : ""}>More &rarr;</button>`;
      document.getElementById("vprev")?.addEventListener("click", () => {
        vState.offset = Math.max(0, vState.offset - VPAGE); void drawVehicles();
      });
      document.getElementById("vnext")?.addEventListener("click", () => {
        vState.offset += VPAGE; void drawVehicles();
      });
    }
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

/**
 * ONE CAR, AND EVERYTHING EVER DONE TO IT.
 *
 * ─── THIS LINK WENT NOWHERE FOR MONTHS ────────────────────────────────
 *
 * The job card has carried "Everything done to this car" pointing at
 * `#/vehicle/<id>` since the vehicle tab was written. The router serves
 * `vehicles` and has never served `vehicle`, so the link fell through
 * every branch and landed on the dashboard -- no error, no 404, just the
 * day in place of the answer. It is the exact shape src/data smoke was
 * built to catch and the one thing it does not look at, so
 * tools/reach-check.mjs now does.
 *
 * ─── WHY IT IS A SCREEN AND NOT A REDIRECT ────────────────────────────
 *
 * The cheap repair was to point the link at the customer, who already has
 * a page listing their jobs. That is a different question. A car outlives
 * its owner's account, gets sold, comes back on a second customer, and the
 * thing an advisor is asking at the counter is "have we had THIS one in
 * before" -- which the customer's list answers wrongly in both directions.
 * `jobs.vehicle_id` has always held the answer and nothing read it.
 *
 * And it is where the manual side belongs. Every source behind /manuals is
 * keyed on a vehicle, so a page about one vehicle is the honest place to
 * put the door to it. See src/domain/manuals.ts.
 */
export async function vehiclePage(id: string, role = ""): Promise<void> {
  const trade = await shopTrade();
  const w = words(trade);
  mount(`<div class="page"><div class="loading">Reading&hellip;</div></div>`);

  type Job = {
    id: string; job_number: string; status: Status; title: string | null;
    complaint: string | null; created_at: string;
    line_items: Array<{ total: number | null }> | null;
  };

  let v: Veh | null = null;
  let jobs: Array<Job & { total: number }> = [];
  try {
    const sb = await db();
    const { data: vr, error: ve } = await sb.from("vehicles")
      .select("id,year,make,model,trim,description,vin,license_plate,mileage,colour,created_at,customers(id,full_name)")
      .eq("id", id).maybeSingle();
    if (ve) throw new Error(ve.message);
    v = (vr ?? null) as unknown as Veh | null;

    if (v) {
      const { data: jr, error: je } = await sb.from("jobs")
        .select("id,job_number,status,title,complaint,created_at,line_items(total)")
        .eq("vehicle_id", id)
        .order("created_at", { ascending: false })
        .limit(100);
      if (je) throw new Error(je.message);
      jobs = ((jr ?? []) as unknown as Job[]).map((j) => ({
        ...j,
        total: (j.line_items ?? []).reduce((n, l) => n + Number(l.total ?? 0), 0),
      }));
    }
  } catch (e) {
    mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/vehicles">&larr; ${esc(cap(w.things))}</a>
        <h1>${esc(cap(w.thing))}</h1></header>
      <div class="card"><div class="empty bad">${
        esc(e instanceof Error ? e.message : String(e))}</div></div></div>`);
    return;
  }

  if (!v) {
    mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/vehicles">&larr; ${esc(cap(w.things))}</a>
        <h1>Not here</h1></header>
      <div class="card"><div class="empty">No ${esc(w.thing)} with that id. It may have been
        archived, or merged into another record.</div></div></div>`);
    return;
  }

  /* The identifiers, each only where the trade has one. A locksmith reading
     "Plate" on a door lock is the same wrongness the rest of this file
     spends its words avoiding. */
  const bits = [
    w.hasPlate && v.license_plate ? esc(v.license_plate) : "",
    v.vin ? `<span class="mono">${esc(v.vin)}</span>` : "",
    w.hasColour && v.colour ? esc(v.colour) : "",
    w.reading && v.mileage ? `${v.mileage.toLocaleString()} mi` : "",
  ].filter(Boolean);

  const spent = jobs.reduce((n, j) => n + j.total, 0);

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/vehicles">&larr; ${esc(cap(w.things))}</a>
      <h1>${esc(name(v, w))}</h1>
      <p>${v.customers
        ? `<a href="#/customer/${esc(v.customers.id)}">${esc(v.customers.full_name)}</a>`
        : `<span class="quiet">No owner on file</span>`}${
        bits.length ? ` &middot; ${bits.join(" &middot; ")}` : ""}</p>
    </header>

    <!-- THE MANUAL SIDE, OPENED ON THIS CAR. Gated on the trade rather
         than the role, the same gate the other two doors to /manuals use:
         behind it is recalls, wiring diagrams and factory portals, and
         every source is a vehicle manufacturer. A new tab, because
         somebody checking a wiring diagram has not finished with this
         page. -->
    ${w.hasPartsCatalog ? `
      <a class="card pad rowlink" href="${esc(manualsHref(v))}" target="_blank" rel="noopener">
        <span class="rowlink-t">Manuals for this ${esc(w.thing)}</span>
        <span class="rowlink-s">${esc(manualsBlurb(v, w.thing))}</span>
        <span class="rowlink-go">&rarr;</span>
      </a>` : ""}

    <div class="card">
      <div class="card-head">Everything done to it &middot; ${jobs.length}${
        spent > 0 ? ` &middot; ${money(spent)}` : ""}</div>
      ${jobs.length ? jobs.map((j) => `
        <a class="row" href="#/job/${esc(j.id)}">
          <div class="row-main">
            <div class="row-title"><span class="num">${esc(j.job_number)}</span>
              <span class="pill s-${esc(j.status)}">${
                esc(STATUS_LABEL[j.status] ?? j.status)}</span></div>
            <div class="row-sub">${esc(j.title || j.complaint || "No description")}</div>
          </div>
          <div class="row-side"><div class="amount">${money(j.total)}</div>
            <div class="when">${esc(since(j.created_at))}</div></div>
        </a>`).join("")
        : `<div class="empty">Nothing on the books for this ${esc(w.thing)} yet.</div>`}
    </div>

    <!-- THE RENTALS CARD, for a shop with a rentals side only. Its rate,
         deposit and terms, and the fleet owner when a partner owns it.
         See src/pages/rentalfleet.ts. -->
    ${hasRentals(trade) ? `<div id="veh-rental"></div>` : ""}

    <p class="fine">On the books since ${esc(ago(v.created_at))}.</p>
  </div>`);
  if (hasRentals(trade)) void rentalCard("veh-rental", v.id, role !== "viewer");
}

/* ── team ─────────────────────────────────────────────────────────── */

/* ── settings ─────────────────────────────────────────────────────── */

export async function settingsPage(role: Role = "viewer"): Promise<void> {
  const mayEdit = MAY_SET_SHOP.includes(role);
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
        <h1>Settings</h1><p>The shop, as the paperwork knows it.${
          mayEdit ? " Click any value to change it." : ""}</p></header>
      <div id="settings"><div class="loading">Reading&hellip;</div></div>
    </div>`);
  try {
    const sb = await db();
    const { data } = await sb.from("accounts")
      .select("id,name,slug,phone,email,address,timezone,labour_rate," +
        "bar_license,epa_id,warranty_terms,trade,review_url").limit(1).maybeSingle();
    const a = data as null | {
      id: string; name: string; phone: string | null; email: string | null;
      address: string | null; timezone: string; labour_rate: number;
      bar_license: string | null; epa_id: string | null;
      warranty_terms: string | null; trade: string | null;
      review_url: string | null;
    };
    const host = document.getElementById("settings");
    if (!host) return;
    if (!a) { host.innerHTML = `<div class="card"><div class="empty">No shop on this login.</div></div>`; return; }

    // Counts, because "how big is this shop" is the question settings is
    // usually opened to answer.
    const c = async (t: string) => (await sb.from(t).select("id", { count: "exact", head: true })).count ?? 0;
    const [cust, veh, jobs, inv] = await Promise.all([c("customers"), c("vehicles"), c("jobs"), c("invoices")]);

    const f = (column: string, value: string | null, placeholder: string,
               multiline = false) =>
      editable({
        table: "accounts", id: a.id, column, value, placeholder, multiline,
        editable: mayEdit,
        reason: "Only the owner or a supervisor changes the shop's own details.",
        save: (next) => updateAccountField(a.id, column, next),
      });

    /* WHAT THE PAPERWORK SAYS ABOUT THIS SHOP.
       Three fields that all print on a customer's invoice and all used to
       be either absent or somebody else's. The warranty was hardcoded to
       Supercanic's terms until 2026-09-18 and went out on four invoices
       from two other businesses; see src/domain/warranty.ts. */
    const warnWarranty = warrantyPrompt(a.warranty_terms, a.name);
    const warnMiles = mileageLooksWrongForTrade(a.warranty_terms, a.trade);

    host.innerHTML = `
      <div class="card pad">
        <div class="kv"><span>Shop</span><b>${f("name", a.name, "Name the shop")}</b></div>
        <div class="kv"><span>Phone</span><b>${f("phone", a.phone, "Add a number")}</b></div>
        <div class="kv"><span>Email</span><b>${f("email", a.email, "Add an email")}</b></div>
        <div class="kv"><span>Address</span><b>${f("address", a.address, "Add the address")}</b></div>
        <div class="kv"><span>Time zone</span><b>${f("timezone", a.timezone, "America/Los_Angeles")}</b></div>
        <div class="kv"><span>Labor rate</span>
          <b>${f("labour_rate", String(a.labour_rate), "0")} <span class="quiet">/ hr</span></b></div>
        <div class="kv"><span>Google review link</span>
          <b>${f("review_url", a.review_url, "Paste your Google review link")}</b></div>
      </div>
      <p class="quiet">The review link is what a paid job texts the customer. Get it from
        your Google Business Profile under Ask for reviews. With none set, no link is sent.</p>
      ${mayEdit ? `<p class="quiet">The labor rate prices every quote from the moment you
        change it. Work already quoted keeps the rate it was quoted at.</p>` : ""}
      <div class="card pad">
        <div class="card-sub">What your paperwork says</div>
        <p class="quiet">These print on every estimate and invoice this shop sends.
           Each one is yours alone &mdash; nothing here is ever borrowed from
           another shop on the system.</p>
        <div class="kv"><span>BAR registration</span>
          <b>${f("bar_license", a.bar_license, "Add your ARD number")}</b></div>
        <div class="kv"><span>EPA ID</span>
          <b>${f("epa_id", a.epa_id, "Add it when you have it")}</b></div>
        <div class="kv kv-tall"><span>Warranty</span>
          <b>${f("warranty_terms", a.warranty_terms,
                 "Say what you warrant, in your own words", true)}</b></div>
        ${warnWarranty ? `<p class="msg">${esc(warnWarranty)}</p>` : ""}
        ${warnMiles ? `<p class="msg warn">Your warranty mentions mileage and this
          shop is not an auto shop. Worth a read &mdash; that wording came from a
          vehicle warranty. It has been saved exactly as you typed it.</p>` : ""}
        <p class="quiet small">The EPA ID prints beside a hazardous waste disposal
           charge, so a customer can see who handled it. Leave it empty until you
           have the real number &mdash; nothing here invents one.</p>
      </div>
      <div class="card pad">
        <div class="card-sub">On the books</div>
        <div class="kv"><span>Customers</span><b>${cust.toLocaleString()}</b></div>
        <div class="kv"><span>${esc(cap(shopWords().things))}</span><b>${veh.toLocaleString()}</b></div>
        <div class="kv"><span>Jobs</span><b>${jobs.toLocaleString()}</b></div>
        <div class="kv"><span>Invoices</span><b>${inv.toLocaleString()}</b></div>
      </div>
      <div class="card pad">
        <div class="card-sub">Your account</div>
        <p class="quiet">Deleting your login removes your way in. It does not remove the work
           you did &mdash; jobs, hours, invoices and photos stay as the shop's own records,
           because a business has to keep them.</p>
        <div class="quiet small">Type DELETE to confirm</div>
        <div class="delrow">
          <input id="del-confirm" autocomplete="off">
          <button class="btn ghost" id="del-go">Delete my login</button>
        </div>
        <div class="msg-slot" id="del-msg"></div>
      </div>
      `;
    bindEditables();

    document.getElementById("del-go")?.addEventListener("click", async () => {
      const b = document.getElementById("del-go") as HTMLButtonElement;
      const msg = document.getElementById("del-msg")!;
      const confirm = (document.getElementById("del-confirm") as HTMLInputElement).value;
      b.disabled = true; b.textContent = "Deleting…";
      try {
        const { data: { session } } = await sb.auth.getSession();
        const res = await fetch(api("/api/account/delete"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
          body: JSON.stringify({ confirm }),
        });
        const body = await res.json() as { ok?: boolean; error?: string };
        if (!res.ok || !body.ok) {
          b.disabled = false; b.textContent = "Delete my login";
          msg.innerHTML = `<p class="msg bad">${esc(body.error ?? "That did not work.")}</p>`;
          return;
        }
        await sb.auth.signOut();
        location.replace("/index.html");
      } catch (e) {
        b.disabled = false; b.textContent = "Delete my login";
        msg.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
      }
    });
  } catch (e) {
    const host = document.getElementById("settings");
    if (host) host.innerHTML = `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/** Shared by the job screen so a status reads the same everywhere. */
export const statusLabel = (s: string) => STATUS_LABEL[s as Status] ?? s;
export { ago, money };
